/* eslint-disable no-param-reassign,@typescript-eslint/ban-ts-comment */
export const flatItems = <Item>(key: string, data?: { [key: string]: any }[]): Item[] | undefined => {
  if (!data) {
    return undefined;
  }
  return data.flatMap(item => item[key]);
};

export const accurateItems = <Item>(key: string, data?: { [key: string]: any }[]): Record<string | number, Item> => {
  if (!data) {
    return {};
  }
  return data.reduce(
    (acc, cur) => {
      const value = cur[key];
      if (value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(acc, value);
      }
      return acc;
    },
    {} as Record<string | number, Item>,
  );
};

export const accurateUniqueItems = <Item>(
  key: string,
  data?: { [key: string]: any }[],
): Record<string | number, Item> => {
  if (!data) {
    return {};
  }

  const uniqueItems = new Set<Item>();
  return data.reduce(
    (acc, cur) => {
      const value = cur[key];
      if (value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
        if (!uniqueItems.has(value)) {
          uniqueItems.add(value);
          Object.assign(acc, value);
        }
      }
      return acc;
    },
    {} as Record<string | number, Item>,
  );
};

export const convertObjectToArray = <K extends string | number | symbol, V>(object: Record<K, V>) =>
  Object.entries(object).map(([key, value]) => ({ key, value })) as { key: K; value: V }[];

export function toMap<T extends { id: number }>(they: T[]): Record<string | number, T> {
  return they.reduce((prev, curr) => {
    // @ts-ignore
    prev[curr.id] = curr;
    return prev;
  }, {});
}

export function toMapByKey<T extends Record<string, any>>(key: string, they: T[]): Record<string | number, T> {
  return they.reduce((prev, curr) => {
    const objKey = curr[key] ?? '';
    // @ts-ignore
    prev[objKey] = curr;
    return prev;
  }, {});
}

export function toArrayMapByKey<T extends Record<string, any>>(key: string, they: T[]): Record<string | number, T[]> {
  const dataMap: Record<string, T[]> = they.map(it => it[key]).reduce((prev, curr) => ({ ...prev, [curr]: [] }), {});
  they.forEach(it => {
    const objKey = it[key] ?? '';
    dataMap[objKey].push(it);
  });
  return dataMap;
}

export const mergeTwo = (array1: number[], array2: number[]) => {
  if (!array1) {
    return array2;
  }
  if (!array2) {
    return array1;
  }

  const merged = Array.from(new Set([...array1, ...array2]));
  return merged.sort((a, b) => a - b);
};

export const deepMerge = (target: Record<string, any>, source: Record<string, any>) => {
  Object.keys(source).map(
    (key: string) => source[key] instanceof Object && Object.assign(source[key], deepMerge(target[key], source[key])),
  );

  Object.assign(target || {}, source);
  return target;
};

export const removeNull = <T>(arr: T[]): NonNullable<T>[] =>
  arr.filter(it => it !== null && it !== undefined) as NonNullable<T>[];

export const toUniqueArray = <T>(arr: T[]) => Array.from(new Set<T>(arr));

export const toUniqueArrayByKey = <T extends Record<string, any>>(key: string, arr: T[]): T[] => {
  const seen = new Set<unknown>();
  return arr.filter(item => !seen.has(item[key]) && seen.add(item[key]));
};

export const range = (count: number, start = 0): number[] => Array.from({ length: count }, (x, i) => i + start);

export const getKeyByValue = <T extends string, V>(
  record: Record<T, V>,
  targetValue: V | undefined,
  defaultKey?: T,
): T | undefined => {
  for (const key in record) {
    if (record[key] === targetValue) {
      return key;
    }
  }
  return defaultKey;
};

export const getSeed = () => Math.floor(Math.random() * 1000000);

export const random = (seed: number = getSeed(), index = 0) => {
  const value = Math.sin(seed + index * 100) * 100000;
  return value - Math.floor(value);
};

export const shuffle = <T>(array: T[], seed = getSeed()) => {
  const result = [...array];

  // https://en.wikipedia.org/wiki/Fisher%E2%80%93Yates_shuffle
  for (let index = result.length - 1; index > 0; index -= 1) {
    const randomPosition = Math.floor(random(seed, index) * (index + 1));

    const temporary = result[index];
    result[index] = result[randomPosition];
    result[randomPosition] = temporary;
  }

  return result;
};

export const chunkArray = <T>(array: T[], chunkSize: number): T[][] =>
  Array.from({ length: Math.ceil(array.length / chunkSize) }, (_, i) =>
    array.slice(i * chunkSize, i * chunkSize + chunkSize),
  );

export const isArraysEqualIgnoreOrder = <T>(arr1: T[], arr2: T[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  if (set1.size !== set2.size) {
    return false;
  }

  return arr1.every(item => set2.has(item));
};

export const hasCommonElements = <T>(arr1: T[], arr2: T[]): boolean => {
  const set2 = new Set(arr2);
  return arr1.some(item => set2.has(item));
};

export const getIntersection = <T>(arr1: T[], arr2: T[]): T[] => {
  if (arr1.length === 0 || arr2.length === 0) {
    return [];
  }

  const [smaller, larger] = arr1.length <= arr2.length ? [arr1, arr2] : [arr2, arr1];
  const largerSet = new Set(larger);

  return smaller.filter(id => largerSet.has(id));
};
